import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetIdentity } from '@refinedev/core';
import { Button } from '@/components/common';
import { FormattedNumberInput } from '@/utils/number-format';
import CustomerSearchInput from './CustomerSearchInput';
import CustomerFinancialInfo from './CustomerFinancialInfo';
import {
  CreateReceiptVoucherRequest,
  UpdateReceiptVoucherRequest,
  ReceiptVoucherResponse,
  ReceiptVoucherFormErrors,
  PAYMENT_METHODS,
  RECEIPT_VOUCHER_CATEGORY_TYPE_CUSTOMER,
} from '@/types/receiptVoucher';
import { Customer } from '@/types/customer';
import { Category, RECEIPT_VOUCHER_CATEGORY_TYPES } from '@/types/category';
import { categoryApi } from '@/api/endpoints/categories';
import { useUIStore } from '@/store/uiStore';

interface ReceiptVoucherFormProps {
  initialData?: ReceiptVoucherResponse;
  onSubmit: (data: CreateReceiptVoucherRequest | UpdateReceiptVoucherRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  mode: 'create' | 'edit';
  initialCustomer?: Customer;
}

const ReceiptVoucherForm: React.FC<ReceiptVoucherFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  mode,
  initialCustomer,
}) => {
  const { t } = useTranslation();
  const { openModal } = useUIStore();
  const { data: identity } = useGetIdentity<{ id: string; name: string; email: string }>();

  // Receipt voucher type categories from API
  const [receiptTypeCategories, setReceiptTypeCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    receipt_type: initialData?.receipt_type || 'customer_payment',
    customer_id: initialData?.customer_id || '',
    payment_method: initialData?.payment_method || 'cash',
    account_number: initialData?.account_number || '',
    transfer_note: initialData?.transfer_note || '',
    note: initialData?.note || '',
    reason: initialData?.reason || '',
    received_now: initialData?.received_now || 0,
    created_at: initialData?.createdAt
      ? formatDateOnly(new Date(initialData.createdAt))
      : formatDateOnly(new Date())
  });

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]); // MongoDB ObjectIDs
  const [selectedInvoiceCodes, setSelectedInvoiceCodes] = useState<string[]>([]); // Invoice codes for BE
  const [errors, setErrors] = useState<ReceiptVoucherFormErrors>({});
  const [inputMode, setInputMode] = useState<'amount' | 'selection' | null>(null); // Track which mode user is in

  // Check if form should be disabled (only allow editing draft status)
  const isFormDisabled = mode === 'edit' && initialData?.current_status?.status !== 'draft';

  // Helper function to check if current receipt type is a customer payment type
  const isCustomerPaymentType = useCallback((receiptType: string): boolean => {
    const category = receiptTypeCategories.find(cat => cat.code === receiptType);
    return category?.type === RECEIPT_VOUCHER_CATEGORY_TYPE_CUSTOMER;
  }, [receiptTypeCategories]);

  // Fetch receipt voucher type categories on mount
  useEffect(() => {
    const fetchReceiptTypeCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await categoryApi.getCategories(1, 100, {
          type: RECEIPT_VOUCHER_CATEGORY_TYPES,
          status: ['active']
        });
        setReceiptTypeCategories(response.data);

        // Set default receipt type if not already set and categories are loaded
        if (response.data.length > 0 && !initialData?.receipt_type) {
          // Default to first customer payment type category if available
          const customerType = response.data.find(cat => cat.type === RECEIPT_VOUCHER_CATEGORY_TYPE_CUSTOMER);
          if (customerType) {
            setFormData(prev => ({ ...prev, receipt_type: customerType.code }));
          } else {
            setFormData(prev => ({ ...prev, receipt_type: response.data[0].code }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch receipt type categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchReceiptTypeCategories();
  }, [initialData?.receipt_type]);

  // Initialize selectedCustomer from initialData when in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData?.customer) {
      // Convert CustomerResponse to Customer type
      const customer: Customer = {
        id: initialData.customer.id,
        code: initialData.customer.code,
        name: initialData.customer.name,
        phone: initialData.customer.phone,
        email: initialData.customer.email || '',
        customerType: 'individual', // Default type, could be determined from data
        address: {
          city: initialData.customer.address?.city || '',
          district: initialData.customer.address?.district || '',
          details: initialData.customer.address?.street_address || '',
        },
        status: 'active', // Default status
        createdBy: '', // Default value
        createdAt: initialData.customer.createdAt || '',
        updatedAt: initialData.customer.updatedAt || '',
        // Optional fields
        gender: initialData.customer.gender as 'male' | 'female' | undefined,
        birthday: initialData.customer.birth,
        facebookLink: initialData.customer.facebook,
        companyName: initialData.customer.company,
        taxCode: initialData.customer.tax,
        notes: initialData.customer.note,
        totalDebt: initialData.customer.debit || 0,
        totalSell: initialData.customer.total_sell || 0,
      };
      setSelectedCustomer(customer);
    } else if (mode === 'create') {
      // Khi tạo mới, nếu có initialCustomer thì preselect, ngược lại reset về null
      if (initialCustomer) {
        setSelectedCustomer(initialCustomer);
      } else {
        setSelectedCustomer(null);
      }
    }
  }, [mode, initialData, initialCustomer]);

  // Clear form data after successful save
  const clearForm = () => {
    setFormData({
      receipt_type: 'customer_payment',
      customer_id: '',
      payment_method: 'cash',
      account_number: '',
      transfer_note: '',
      note: '',
      reason: '',
      received_now: 0,
      created_at: formatDateOnly(new Date())
    });
    setSelectedCustomer(null);
    setErrors({});
  };

  // Update customer_id when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setFormData(prev => ({ ...prev, customer_id: selectedCustomer.code }));
    } else {
      setFormData(prev => ({ ...prev, customer_id: '' }));
    }
  }, [selectedCustomer]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof ReceiptVoucherFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Custom handler for received_now input to track mode
  const handleReceivedNowChange = (value: number) => {
    if (value > 0 && inputMode !== 'amount') {
      setInputMode('amount');
    } else if (value === 0 && inputMode === 'amount') {
      setInputMode(null);
      setSelectedInvoiceIds([]); // Clear selection when clearing amount in amount mode
    }
    handleInputChange('received_now', value);
  };

  // Custom handler for invoice selection to track mode
  const handleInvoiceSelectionChange = (ids: string[], codes: string[]) => {
    // Only change mode if we're not already in amount mode
    // (amount mode auto-selects checkboxes, don't switch to selection mode)
    if (inputMode !== 'amount') {
      if (ids.length > 0 && inputMode !== 'selection') {
        setInputMode('selection');
      } else if (ids.length === 0 && inputMode === 'selection') {
        setInputMode(null);
        handleInputChange('received_now', 0); // Clear amount when clearing selection in selection mode
      }
    }
    setSelectedInvoiceIds(ids);
    setSelectedInvoiceCodes(codes);
  };

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    // Clear customer error when customer is selected
    if (customer && errors.customer_id) {
      setErrors(prev => ({ ...prev, customer_id: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ReceiptVoucherFormErrors = {};

    if (!formData.receipt_type) {
      newErrors.receipt_type = t('receiptVoucher.form.errors.receiptTypeRequired');
    }

    // Customer is only required for customer payment type (based on category type)
    if (isCustomerPaymentType(formData.receipt_type) && !selectedCustomer) {
      newErrors.customer_id = t('receiptVoucher.form.errors.customerRequired');
    }

    if (!formData.payment_method) {
      newErrors.payment_method = t('receiptVoucher.form.errors.paymentMethodRequired');
    }

    if (formData.payment_method === 'transfer' && !formData.account_number.trim()) {
      newErrors.account_number = t('receiptVoucher.form.errors.accountNumberRequired');
    }

    if (formData.received_now <= 0) {
      newErrors.received_now = t('receiptVoucher.form.errors.receivedNowRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent submission if form is disabled (non-draft status)
    if (isFormDisabled) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      // Convert datetime-local to RFC3339 format for backend
      const createdAtRFC3339 = new Date(formData.created_at).toISOString();

      // Add invoice_ids to the form data
      const submitData: CreateReceiptVoucherRequest | UpdateReceiptVoucherRequest = {
        ...formData,
        customer_id: isCustomerPaymentType(formData.receipt_type) ? formData.customer_id : undefined,
        invoice_ids: selectedInvoiceCodes.length > 0 ? selectedInvoiceCodes : undefined,
        created_at: mode === 'create' ? createdAtRFC3339 : undefined // Only send created_at when creating
      };

      await onSubmit(submitData);
      // Clear form after successful submission if in create mode
      if (mode === 'create') {
        clearForm();
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Status Warning for Non-Draft Receipt Vouchers */}
      {isFormDisabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <h3 className="text-sm font-medium text-yellow-800">
            {t('receiptVoucher.form.statusWarning.title')}
          </h3>
          <p className="mt-1 text-sm text-yellow-700">
            {t('receiptVoucher.form.statusWarning.message', {
              status: initialData?.current_status?.status
            })}
          </p>
        </div>
      )}

      {/* Basic Information - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Receipt Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('receiptVoucher.form.receiptType')} <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.receipt_type}
            onChange={(e) => handleInputChange('receipt_type', e.target.value)}
            className={`
              block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
              ${errors.receipt_type ? 'border-red-300' : 'border-gray-300'}
              ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
            `}
            disabled={loading || isFormDisabled || loadingCategories}
          >
            {loadingCategories ? (
              <option value="">{t('common.loading')}</option>
            ) : receiptTypeCategories.length === 0 ? (
              <option value="">{t('receiptVoucher.form.noReceiptTypesAvailable')}</option>
            ) : (
              receiptTypeCategories.map(category => (
                <option key={category.code} value={category.code}>
                  {category.name}
                </option>
              ))
            )}
          </select>
          {errors.receipt_type && (
            <p className="mt-1 text-sm text-red-600">{errors.receipt_type}</p>
          )}
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('receiptVoucher.form.paymentMethod')} <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.payment_method}
            onChange={(e) => handleInputChange('payment_method', e.target.value)}
            className={`
              block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
              ${errors.payment_method ? 'border-red-300' : 'border-gray-300'}
              ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
            `}
            disabled={loading || isFormDisabled}
          >
            {PAYMENT_METHODS.map(method => (
              <option key={method.value} value={method.value}>
                {t(`receiptVoucher.paymentMethods.${method.value}`)}
              </option>
            ))}
          </select>
          {errors.payment_method && (
            <p className="mt-1 text-sm text-red-600">{errors.payment_method}</p>
          )}
        </div>

        {/* Customer Selection - only show for customer payment type categories */}
        {isCustomerPaymentType(formData.receipt_type) && (
          <div className="md:col-span-1">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('receiptVoucher.form.customer')} <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => openModal('createCustomer')}
                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || isFormDisabled}
              >
                {t('receiptVoucher.form.createCustomer')}
              </button>
            </div>
            <CustomerSearchInput
              selectedCustomer={selectedCustomer}
              onCustomerChange={handleCustomerChange}
              disabled={loading || isFormDisabled}
              error={errors.customer_id}
            />
          </div>
        )}
      </div>

      {/* Creator and Time Information - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Creator Name - Read Only */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Người tạo
          </label>
          <input
            type="text"
            value={mode === 'create' ? (identity?.name || 'Đang tải...') : (initialData?.created_by_name || initialData?.created_by || '')}
            readOnly
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600 cursor-not-allowed sm:text-sm"
          />
        </div>

        {/* Creation Date - Editable */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Thời gian
          </label>
          <input
            type="date"
            value={formData.created_at}
            onChange={(e) => handleInputChange('created_at', e.target.value)}
            className={`
              block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
              ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
            `}
            disabled={loading || isFormDisabled}
          />
        </div>
      </div>

      {/* Transfer Information (only show if payment method is transfer) */}
      {formData.payment_method === 'transfer' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('receiptVoucher.form.accountNumber')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => handleInputChange('account_number', e.target.value)}
              className={`
                block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
                ${errors.account_number ? 'border-red-300' : 'border-gray-300'}
                ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
              `}
              disabled={loading || isFormDisabled}
            />
            {errors.account_number && (
              <p className="mt-1 text-sm text-red-600">{errors.account_number}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('receiptVoucher.form.transferNote')}
            </label>
            <input
              type="text"
              value={formData.transfer_note}
              onChange={(e) => handleInputChange('transfer_note', e.target.value)}
              className={`
                block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
                ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
              `}
              disabled={loading || isFormDisabled}
            />
          </div>
        </div>
      )}

      {/* Payment Input - always show */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('receiptVoucher.form.receivedNow')} <span className="text-red-500">*</span>
        </label>
        <FormattedNumberInput
          value={formData.received_now}
          onChange={handleReceivedNowChange}
          className={`
            block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
            ${errors.received_now ? 'border-red-300' : 'border-gray-300'}
            ${isFormDisabled || inputMode === 'selection' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
          `}
          disabled={loading || isFormDisabled || inputMode === 'selection'}
          placeholder="0"
        />
        {errors.received_now && (
          <p className="mt-1 text-sm text-red-600">{errors.received_now}</p>
        )}
        {inputMode === 'selection' && (
          <p className="mt-1 text-xs text-gray-500">
            {t('receiptVoucher.form.autoCalculatedFromSelection')}
          </p>
        )}
      </div>

      {/* Customer Financial Information - only show for customer payment type categories */}
      {isCustomerPaymentType(formData.receipt_type) && selectedCustomer && (
        <CustomerFinancialInfo
          customer={selectedCustomer}
          selectedInvoiceIds={selectedInvoiceIds}
          onInvoiceSelectionChange={handleInvoiceSelectionChange}
          receivedAmount={formData.received_now}
          onReceivedAmountChange={(amount) => handleInputChange('received_now', amount)}
          inputMode={inputMode}
        />
      )}

      {/* Reason - Compact */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Lý do thu tiền
        </label>
        <textarea
          value={formData.reason}
          onChange={(e) => handleInputChange('reason', e.target.value)}
          rows={2}
          className={`
            block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
            ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
          `}
          placeholder="Nhập lý do thu tiền"
          disabled={loading || isFormDisabled}
        />
      </div>

      {/* Note - Compact */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('receiptVoucher.form.note')}
        </label>
        <textarea
          value={formData.note}
          onChange={(e) => handleInputChange('note', e.target.value)}
          rows={2}
          className={`
            block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
            ${isFormDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
          `}
          placeholder={!isCustomerPaymentType(formData.receipt_type) ? t('receiptVoucher.form.fundDepositNotePlaceholder') : t('receiptVoucher.form.notePlaceholder')}
          disabled={loading || isFormDisabled}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={isFormDisabled}
        >
          {mode === 'create' ? t('receiptVoucher.form.create') : t('receiptVoucher.form.update')}
        </Button>
      </div>
    </form>
  );
};

export default ReceiptVoucherForm;
